import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Container } from "@/components/layout/Container";
import { PageHeader } from "@/components/content/PageHeader";
import { StudyModeCard } from "@/components/content/StudyModeCard";
import { PaywallNotice } from "@/components/content/PaywallNotice";
import { Button } from "@/components/ui/button";
import { STUDY_MODES, routeForMode } from "@/lib/study-modes";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChapterContext } from "@/hooks/useChapterContext";
import { useMySubscriptions } from "@/hooks/useSubscription";
import { StaggerList, StaggerItem } from "@/components/motion/Stagger";
import { useAITutorStore } from "@/stores/ai-tutor-store";

export function StudyModePage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { status } = useAuth();
  const { data: me } = useCurrentUser();
  const mySubs = useMySubscriptions();
  const { data: ctx } = useChapterContext(chapterId);
  const [searchParams, setSearchParams] = useSearchParams();
  const openTutor = useAITutorStore((s) => s.open);

  // The AI Tutor tile uses a query-string deep link so the chapter picker can
  // remain the active route underneath the sheet. Consume that intent here,
  // pin the current chapter, then remove it so closing the sheet does not
  // immediately reopen it on the next render/back navigation.
  useEffect(() => {
    if (!chapterId || searchParams.get("tutor") !== "open") return;
    openTutor({ chapterId });
    const next = new URLSearchParams(searchParams);
    next.delete("tutor");
    setSearchParams(next, { replace: true });
  }, [chapterId, openTutor, searchParams, setSearchParams]);

  // Chapter context comes from /curriculum/chapters/:id which is still
  // unbuilt -- the hook degrades to null on 404. The viewer pages do their
  // OWN entitlement check via the signed-URL endpoint, so the picker
  // doesn't need to gate-keep here. We render the tile grid immediately;
  // only show the paywall banner if we KNOW the chapter is premium AND
  // the user doesn't qualify.
  const chapter = ctx?.chapter ?? null;
  const subjectName = ctx?.subject?.subject_name ?? null;
  const activeSub = mySubs.data?.active ?? null;
  const subscribedClassId =
    activeSub?.class ?? (me?.active_subscription_id ? me?.class_id ?? null : null);
  const targetClassLabel = chapter ? classLabel(chapter.class_id) : "this class";
  const subscribedClassLabel = subscribedClassId ? classLabel(subscribedClassId) : "your subscribed class";
  const subscribedClassHref = subscribedClassId
    ? `/subjects?class=${encodeURIComponent(subscribedClassId)}`
    : "/subjects";

  const subscribed = status === "authenticated" && Boolean(activeSub || me?.active_subscription_id);
  const userClassMatches = chapter ? subscribedClassId === chapter.class_id : true;
  const wrongClass = chapter != null && chapter.is_demo !== true && subscribed && !userClassMatches;
  const knownLocked =
    chapter != null && !(chapter.is_demo === true || (subscribed && userClassMatches));

  return (
    <Container size="lg" className="py-8 lg:py-12">
      <PageHeader
        title={chapter?.chapter_name || "Pick a study mode"}
        description="Switch between modes any time — your progress stays in sync."
        crumbs={[
          { to: "/subjects", label: "Subjects" },
          ...(subjectName ? [{ label: subjectName }] : []),
          { label: chapter?.chapter_name || "Chapter" },
        ]}
      />

      {knownLocked ? (
        wrongClass ? (
          <PaywallNotice
            className="mb-6"
            title={`${targetClassLabel} is not included in your plan`}
            body={`Your current subscription covers ${subscribedClassLabel}. You can browse ${targetClassLabel} free chapters, but premium study modes require a separate class access change.`}
            action={
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button variant="accent" size="sm" asChild>
                  <Link to={subscribedClassHref}>Go to {subscribedClassLabel}</Link>
                </Button>
                <Button variant="secondary" size="sm" asChild>
                  <Link to="/billing">Manage subscription</Link>
                </Button>
              </div>
            }
          />
        ) : (
          <PaywallNotice className="mb-6" classId={chapter?.class_id} />
        )
      ) : null}

      <StaggerList className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {STUDY_MODES.map((m) => {
          const locked = !m.alwaysAccessible && knownLocked;
          const href = `/study/${encodeURIComponent(chapterId || "")}${routeForMode(m.id)}`;
          return (
            <StaggerItem key={m.id}>
              <StudyModeCard mode={m} href={href} locked={locked} />
            </StaggerItem>
          );
        })}
      </StaggerList>
    </Container>
  );
}

function classLabel(classId: string): string {
  const n = classId.replace(/^class-/, "");
  return /^\d+$/.test(n) ? `Class ${n}` : classId;
}
